import React, { Component } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  PictureOutlined,
  ReloadOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { aiConfigApi } from '../../../../lib/aiConfigApi';

const { Text, Title } = Typography;
const { Option } = Select;

const formatDateTime = (value) => {
  if (!value) return 'Chưa có dữ liệu thời gian';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Chưa có dữ liệu thời gian';
  return date.toLocaleString('vi-VN');
};

export default class ProductKnowledgeTab extends Component {
  state = {
    loading: false,
    stats: null,
    error: '',
  };

  componentDidMount() {
    this.loadStats();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.companyId !== this.props.companyId) this.loadStats();
  }

  loadStats = async () => {
    const { companyId } = this.props;
    if (!companyId) return;

    this.setState({ loading: true, error: '' });
    try {
      const response = await aiConfigApi.getProductKnowledgeStats(companyId);
      this.setState({ stats: response.data || null });
    } catch (error) {
      this.setState({ error: error?.message || 'Không thể tải trạng thái kiến thức sản phẩm' });
    } finally {
      this.setState({ loading: false });
    }
  };

  getToolConfig = () => {
    const productCatalog = this.props.config?.tools?.product_catalog || {};
    return {
      enabled: productCatalog.enabled !== false,
      max_items: productCatalog.max_items || 5,
    };
  };

  columns = [
    {
      title: 'Sản phẩm',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (title) => <Text strong>{title}</Text>,
    },
    {
      title: 'Danh mục',
      dataIndex: 'category',
      key: 'category',
      width: 180,
      render: (category) => category ? <Tag color="blue">{category}</Tag> : <Text type="secondary">Chưa phân loại</Text>,
    },
    {
      title: 'Ảnh đã quét',
      dataIndex: 'imageCount',
      key: 'imageCount',
      width: 130,
      align: 'center',
      render: (count) => <span><PictureOutlined style={{ color: '#1890ff', marginRight: 6 }} />{count || 0}</span>,
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 150,
      render: () => <Tag color="green"><CheckCircleOutlined /> Đã lập chỉ mục</Tag>,
    },
    {
      title: 'Cập nhật',
      dataIndex: 'indexedAt',
      key: 'indexedAt',
      width: 185,
      render: (value) => <Text type="secondary">{formatDateTime(value)}</Text>,
    },
  ];

  render() {
    const { config, onConfigChange, onSave, onToolChange, saving } = this.props;
    const { loading, stats, error } = this.state;
    const toolConfig = this.getToolConfig();
    const items = stats?.items || [];
    const indexed = Number(stats?.products || 0);

    return (
      <div style={{ padding: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
          <div style={{ maxWidth: 720 }}>
            <Title level={4} style={{ margin: '0 0 4px' }}>Kiến thức sản phẩm</Title>
            <Text type="secondary">
              Catalog được lập chỉ mục riêng để Agent tư vấn về sản phẩm và đối chiếu ảnh khách hàng gửi. Tài liệu tải lên thủ công nằm ở tab Kiến thức chung.
            </Text>
          </div>
          <Space>
            <Tag color={indexed > 0 ? 'green' : 'default'}>
              {indexed > 0 ? <CheckCircleOutlined /> : null} {indexed > 0 ? 'Đã lập chỉ mục' : 'Chưa có dữ liệu'}
            </Tag>
            <Button icon={<ReloadOutlined />} onClick={this.loadStats} loading={loading}>Làm mới</Button>
          </Space>
        </div>

        {error && <Alert style={{ marginBottom: 16 }} type="error" showIcon message="Không thể tải chỉ mục sản phẩm" description={error} />}
        {indexed > 0 && !stats?.lastIndexedAt && (
          <Alert
            style={{ marginBottom: 16 }}
            type="info"
            showIcon
            message="Catalog đã nạp nhưng chưa có dấu thời gian"
            description="Hãy nạp lại kiến thức sản phẩm một lần để lưu thời điểm đồng bộ cho các lần sau."
          />
        )}

        <Card
          size="small"
          title={<span><SettingOutlined style={{ color: '#1890ff', marginRight: 8 }} />Công cụ Agent</span>}
          extra={<Button type="primary" onClick={onSave} loading={saving}>Lưu cấu hình Agent</Button>}
          style={{ marginBottom: 16 }}
        >
          <Row gutter={[24, 16]} align="middle">
            <Col xs={24} md={14}>
              <Space align="start" size={12}>
                <Switch checked={toolConfig.enabled} onChange={(enabled) => onToolChange({ enabled })} />
                <div>
                  <Text strong>Cho phép Agent tra cứu catalog sản phẩm</Text>
                  <div><Text type="secondary">Dùng khi khách hỏi có bán sản phẩm, giá, biến thể hoặc gửi ảnh cần đối chiếu.</Text></div>
                </div>
              </Space>
            </Col>
            <Col xs={24} md={10}>
              <Space direction="vertical" size={2} style={{ width: '100%' }}>
                <Text strong>Sản phẩm tối đa mỗi lần tra cứu</Text>
                <InputNumber min={1} max={10} value={toolConfig.max_items} onChange={(max_items) => onToolChange({ max_items: max_items || 1 })} style={{ width: '100%' }} disabled={!toolConfig.enabled} />
                <Text type="secondary">Giới hạn kết quả đưa vào ngữ cảnh để kiểm soát chi phí token.</Text>
              </Space>
            </Col>
          </Row>
          <div style={{ borderTop: '1px solid #f0f0f0', margin: '20px 0' }} />
          <Row gutter={[24, 16]} align="middle">
            <Col xs={24} md={14}>
              <Space align="start" size={12}>
                <Switch
                  checked={!!config?.order_tools_enabled}
                  onChange={(enabled) => onConfigChange('order_tools_enabled', enabled)}
                />
                <div>
                  <Text strong>Cho phép Agent dùng tools sản phẩm và đơn hàng</Text>
                  <div>
                    <Text type="secondary">
                      Bật quyền gọi API tìm sản phẩm, tạo đơn nháp và xác nhận đơn trong workspace này.
                    </Text>
                  </div>
                </div>
              </Space>
            </Col>
            <Col xs={24} md={10}>
              <Space direction="vertical" size={2} style={{ width: '100%' }}>
                <Text strong>Tool được phép sử dụng</Text>
                <Select
                  mode="multiple"
                  value={config?.order_tool_allowlist || []}
                  onChange={(value) => onConfigChange('order_tool_allowlist', value)}
                  placeholder="Chọn tool cho Agent"
                  disabled={!config?.order_tools_enabled}
                  style={{ width: '100%' }}
                >
                  <Option value="search_products">Tìm kiếm sản phẩm</Option>
                  <Option value="create_order_draft">Tạo đơn hàng nháp</Option>
                  <Option value="confirm_order">Xác nhận đơn hàng</Option>
                </Select>
                <Text type="secondary">Chỉ các tool được chọn mới có thể được Agent gọi.</Text>
              </Space>
            </Col>
          </Row>
        </Card>

        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={8}>
            <Card size="small"><Statistic title="Sản phẩm trong chỉ mục" value={indexed} prefix={<DatabaseOutlined />} /></Card>
          </Col>
          <Col xs={24} md={8}>
            <Card size="small"><Statistic title="Hình ảnh đã quét" value={Number(stats?.images || 0)} prefix={<PictureOutlined />} valueStyle={{ color: '#52c41a' }} /></Card>
          </Col>
          <Col xs={24} md={8}>
            <Card size="small">
              <Text type="secondary"><ClockCircleOutlined style={{ marginRight: 6 }} />Nạp gần nhất</Text>
              <div style={{ marginTop: 8, fontSize: 16, fontWeight: 600 }}>{formatDateTime(stats?.lastIndexedAt)}</div>
            </Card>
          </Col>
        </Row>

        <Card title="Chỉ mục sản phẩm" extra={<Text type="secondary">Nạp/cập nhật tại màn Quản lý sản phẩm</Text>}>
          <Spin spinning={loading}>
            <Table
              columns={this.columns}
              dataSource={items}
              rowKey="productId"
              size="small"
              pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total) => `Tổng ${total} sản phẩm` }}
              locale={{ emptyText: <Empty description="Chưa có sản phẩm được nạp vào kho tri thức" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            />
          </Spin>
        </Card>
      </div>
    );
  }
}
